// DonateSection organism component
import { lazy, Suspense, useState } from "react";
import { useLandingContent } from "../../contexts/LandingContentContext";
import { Heading, Text, Button, Input } from "../atoms";
import { QUICK_DONATION_AMOUNTS } from "../../utils/constants";
import { donationService } from "../../services/donationService";
import { isStripeConfigured } from "../../services/stripe";

// Lazy-loaded so @stripe/* + Stripe.js (~245KB) only ship to users who
// actually start a payment.
const StripePaymentForm = lazy(() => import("./StripePaymentForm"));

export const DonateSection = () => {
  const { donate } = useLandingContent();
  const [donationAmount, setDonationAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const handleQuickAmount = (amount) => {
    setDonationAmount(amount);
    setShowForm(true);
  };

  const handlePaid = async (paymentIntentId) => {
    try {
      await donationService.confirmDonation(paymentIntentId);
      setMessage(`Thank you for your donation of £${donationAmount}!`);
      setClientSecret("");
      setDonationAmount("");
      setDonorName("");
      setDonorEmail("");
      setShowForm(false);
      setTimeout(() => setMessage(""), 5000);
    } catch (err) {
      setError(err.message || "We could not confirm your donation.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");

    if (!donationAmount || parseFloat(donationAmount) <= 0) {
      setError("Please enter a valid donation amount");
      return;
    }

    if (!donorName.trim()) {
      setError("Please enter your name");
      return;
    }

    if (!donorEmail.trim()) {
      setError("Please enter your email");
      return;
    }

    if (!isStripeConfigured()) {
      setError("Payments are not yet configured. Please check back soon.");
      return;
    }

    try {
      setLoading(true);
      const { clientSecret: secret } =
        await donationService.createPaymentIntent(
          donorName.trim(),
          donorEmail.trim(),
          parseFloat(donationAmount),
        );
      setClientSecret(secret);
    } catch (err) {
      setError(err.message || "Could not start the payment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="donate" className="py-24 bg-cream">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-14">
          <p className="font-display text-terracotta-600 text-sm uppercase tracking-widest mb-3">
            Make a Difference
          </p>
          <Heading level={2} className="font-display text-ink mb-5">
            {donate.heading}
          </Heading>
          <Text
            size="xl"
            className="text-ink-soft max-w-xl mx-auto leading-relaxed"
          >
            {donate.body}
          </Text>
        </div>

        {/* Donate card */}
        <div className="max-w-md mx-auto bg-paper rounded-2xl shadow-lift border border-terracotta-50 overflow-hidden">
          {/* Card accent bar */}
          <div className="h-1.5 bg-gradient-to-r from-terracotta-400 via-terracotta-600 to-terracotta-400" />

          <div className="p-10">
            {!showForm ? (
              <>
                <p className="font-display text-ink text-lg mb-6 text-center">
                  Choose an amount
                </p>

                {/* Quick-amount grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {QUICK_DONATION_AMOUNTS.map((amt) => (
                    <Button
                      key={amt}
                      variant="secondary"
                      onClick={() => handleQuickAmount(amt)}
                      className="py-3 font-display text-base border border-terracotta-200 hover:border-terracotta-500 hover:bg-terracotta-50 transition-colors"
                    >
                      £{amt}
                    </Button>
                  ))}
                </div>

                {/* Divider */}
                <div className="flex items-center gap-3 mb-6">
                  <div className="flex-1 h-px bg-terracotta-100" />
                  <span className="text-xs text-ink-soft uppercase tracking-widest">
                    or enter amount
                  </span>
                  <div className="flex-1 h-px bg-terracotta-100" />
                </div>

                <Input
                  type="number"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  placeholder="Custom Amount (£)"
                  className="mb-6 text-center text-xl"
                />

                <Button
                  variant="primary"
                  className="w-full py-4 text-base font-display tracking-wide"
                  onClick={() => {
                    if (donationAmount && parseFloat(donationAmount) > 0) {
                      setShowForm(true);
                    } else {
                      setError("Please enter a valid amount");
                    }
                  }}
                >
                  Continue to Donate
                </Button>
              </>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Selected amount display */}
                <div className="flex items-center justify-between bg-terracotta-50 rounded-xl px-5 py-4 mb-2">
                  <div>
                    <p className="text-xs text-ink-soft uppercase tracking-widest mb-0.5">
                      Donation amount
                    </p>
                    <p className="font-display text-2xl text-terracotta-700 font-semibold">
                      £{donationAmount}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="text-sm text-terracotta-600 hover:text-terracotta-800 underline underline-offset-2 transition-colors"
                  >
                    Change
                  </button>
                </div>

                <div className="text-left">
                  <label className="block text-sm font-semibold text-ink mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="Enter your name"
                    required
                  />
                </div>

                <div className="text-left">
                  <label className="block text-sm font-semibold text-ink mb-2">
                    Your Email
                  </label>
                  <Input
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                {/* Security note */}
                <p className="flex items-center gap-2 text-xs text-ink-soft">
                  <svg
                    className="w-3.5 h-3.5 text-ink-teal flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  Your information is encrypted and secure.
                </p>

                {!clientSecret ? (
                  <Button
                    variant="primary"
                    type="submit"
                    className="w-full py-4 text-lg"
                    disabled={loading}
                  >
                    {loading ? "Starting..." : "Continue to Payment"}
                  </Button>
                ) : (
                  <Suspense
                    fallback={
                      <p className="text-ink-soft text-sm py-4">
                        Loading secure payment…
                      </p>
                    }
                  >
                    <StripePaymentForm
                      clientSecret={clientSecret}
                      amount={donationAmount}
                      onPaid={handlePaid}
                      onError={setError}
                    />
                  </Suspense>
                )}
              </form>
            )}

            {message && (
              <p className="mt-5 text-center text-ink-teal font-semibold bg-terracotta-50 rounded-xl px-5 py-4">
                {message}
              </p>
            )}
            {error && (
              <p className="mt-4 text-center text-red-700 text-sm font-medium bg-red-50 rounded-lg px-4 py-3">
                {error}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
